import {
  createContext,
  useContext,
  useEffect,
  useMemo,
  useState,
  type ReactNode,
} from "react";
import { onAuthChange, type AuthUser } from "@/lib/api";
import { setAnalyticsUserId } from "./analytics";
import { setCrashlyticsUserId } from "./crashlytics";

interface AuthContextValue {
  user: AuthUser | null;
  /** True until the first onAuthStateChanged fires — avoids flash-of-signed-out. */
  loading: boolean;
}

const AuthContext = createContext<AuthContextValue | null>(null);

export function AuthProvider({ children }: { children: ReactNode }) {
  const [user, setUser] = useState<AuthUser | null>(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    return onAuthChange((u) => {
      setUser(u);
      setLoading(false);
      setAnalyticsUserId(u?.uid ?? null);
      setCrashlyticsUserId(u?.uid ?? null);
    });
  }, []);

  const value = useMemo<AuthContextValue>(
    () => ({ user, loading }),
    [user, loading],
  );

  return <AuthContext.Provider value={value}>{children}</AuthContext.Provider>;
}

export function useAuth(): AuthContextValue {
  const ctx = useContext(AuthContext);
  if (!ctx) throw new Error("useAuth must be used inside <AuthProvider>");
  return ctx;
}
